import React from 'react';
import Navigation from '../components/Navigation';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Info, Mail, Shield, Cpu } from 'lucide-react';
import { usePageView } from '../hooks/usePageView';

const About = ({ user, setUser }) => {
  usePageView('about');

  return (
    <div className="about-container">
      <Navigation user={user} setUser={setUser} currentPage="about" />
      
      <div className="about-content">
        <div className="page-header">
          <h1 data-testid="about-title">About Baad.tv</h1>
          <p>Learn more about our TV show tracking platform</p>
        </div>

        <div className="about-sections">
          <Card className="about-card">
            <CardHeader>
              <div className="card-header-with-icon">
                <Info className="header-icon" />
                <CardTitle>What is Baad.tv?</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p>
                Baad.tv is your personal TV show tracking and social review platform. 
                Keep track of what you're watching, share reviews with your flock, 
                and discover new shows through your friends' recommendations.
              </p>
              <br />
              <p>
                Whether you're currently watching a series, waiting for the next season, 
                or building your watchlist, Baad.tv helps you organize your viewing 
                experience and connect with fellow TV enthusiasts.
              </p>
            </CardContent>
          </Card>

          <Card className="about-card">
            <CardHeader>
              <div className="card-header-with-icon">
                <Cpu className="header-icon" />
                <CardTitle>AI-Powered Development</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p className="disclaimer-text">
                <strong>Disclaimer:</strong> Baad.tv was built with the assistance of 
                artificial intelligence technology. While we strive for the highest 
                quality and reliability, please note that AI-assisted development was 
                used in creating this application.
              </p>
              <br />
              <p>
                We continuously work to improve the platform and appreciate your feedback 
                as we enhance the user experience.
              </p>
            </CardContent>
          </Card>

          <Card className="about-card">
            <CardHeader>
              <div className="card-header-with-icon">
                <Shield className="header-icon" />
                <CardTitle>Copyright & Legal</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p>
                <strong>© {new Date().getFullYear()} Baad.tv.</strong> All rights reserved.
              </p>
              <br />
              <p>
                This application is protected by copyright law. Unauthorized reproduction, 
                distribution, or modification of this software, in whole or in part, is 
                strictly prohibited.
              </p>
              <br />
              <p className="disclaimer-text">
                <strong>Data Attribution:</strong> TV show information, posters, and metadata 
                are provided by The Movie Database (TMDB). Baad.tv uses the TMDB API but is 
                not endorsed or certified by TMDB.
              </p>
              <br />
              <p className="disclaimer-text">
                <strong>Third-Party Services:</strong> This application uses Google OAuth 
                for authentication. By using Baad.tv, you agree to Google's Terms of Service 
                and Privacy Policy.
              </p>
            </CardContent>
          </Card>

          <Card className="about-card">
            <CardHeader>
              <div className="card-header-with-icon">
                <Shield className="header-icon" />
                <CardTitle>Privacy Policy</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p>
                <strong>Last Updated:</strong> {new Date().toLocaleDateString()}
              </p>
              <br />
              <p>
                At Baad.tv, we take your privacy seriously. This Privacy Policy explains how we 
                collect, use, and protect your personal information.
              </p>
              <br />
              
              <div className="privacy-section">
                <h4>Information We Collect</h4>
                <p>
                  When you use Baad.tv, we collect the following information:
                </p>
                <ul>
                  <li><strong>Account Information:</strong> Name, email address, and profile picture from your Google account</li>
                  <li><strong>User Content:</strong> TV shows you add, your ratings, reviews, and status updates</li>
                  <li><strong>Social Data:</strong> Your friends/followers on the platform and your interactions with their content</li>
                  <li><strong>Usage Data:</strong> Pages visited and features used to improve the platform</li>
                </ul>
              </div>
              <br />

              <div className="privacy-section">
                <h4>How We Use Your Information</h4>
                <p>We use your information to:</p>
                <ul>
                  <li>Provide and maintain the Baad.tv service</li>
                  <li>Display your profile, reviews, and show tracking to your friends</li>
                  <li>Calculate statistics and recommendations</li>
                  <li>Improve and personalize your experience</li>
                  <li>Communicate with you about the service</li>
                </ul>
              </div>
              <br />

              <div className="privacy-section">
                <h4>Data Sharing and Security</h4>
                <p>
                  <strong>We do not sell your personal information.</strong> Your data is shared only with:
                </p>
                <ul>
                  <li>Friends you choose to follow within the platform</li>
                  <li>Google OAuth for authentication purposes</li>
                  <li>The Movie Database (TMDB) for show information</li>
                </ul>
                <p>
                  We implement security measures to protect your data, but no method of 
                  transmission over the internet is 100% secure.
                </p>
              </div>
              <br />

              <div className="privacy-section">
                <h4>Your Rights</h4>
                <p>You have the right to:</p>
                <ul>
                  <li>Access and update your personal information</li>
                  <li>Delete your account and associated data</li>
                  <li>Control who can see your content through blocking features</li>
                  <li>Request a copy of your data</li>
                </ul>
              </div>
              <br />

              <div className="privacy-section">
                <h4>Cookies and Tracking</h4>
                <p>
                  We use cookies and similar technologies to maintain your session and 
                  improve your experience. These are essential for the platform to function properly.
                </p>
              </div>
              <br />

              <div className="privacy-section">
                <h4>Changes to This Policy</h4>
                <p>
                  We may update this Privacy Policy from time to time. We will notify users of 
                  any material changes by updating the "Last Updated" date.
                </p>
              </div>
              <br />

              <p className="disclaimer-text">
                <strong>Questions?</strong> If you have any questions about this Privacy Policy, 
                please contact us at <a href="mailto:privacy@baad.tv" className="email-link">privacy@baad.tv</a>
              </p>
            </CardContent>
          </Card>

          <Card className="about-card">
            <CardHeader>
              <div className="card-header-with-icon">
                <Mail className="header-icon" />
                <CardTitle>Contact & Feedback</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p>
                We'd love to hear from you! Whether you have feedback, questions, or need support, 
                feel free to reach out to us.
              </p>
              <br />
              
              <div className="contact-section">
                <div className="contact-item">
                  <strong>General Feedback:</strong>
                  <a href="mailto:feedback@baad.tv" className="email-link">
                    feedback@baad.tv
                  </a>
                </div>

                <div className="contact-item">
                  <strong>Support, Help and Bug Reports:</strong>
                  <a href="mailto:support@baad.tv" className="email-link">
                    support@baad.tv
                  </a>
                </div>
              </div>

              <br />
              <p className="response-note">
                We aim to respond to all inquiries within 48 hours. Please include as much 
                detail as possible in your message to help us assist you better.
              </p>
            </CardContent>
          </Card>

          <Card className="about-card version-card">
            <CardContent className="version-content">
              <div className="version-info">
                <span className="version-label">Version:</span>
                <span className="version-number">1.0.0</span>
              </div>
              <div className="version-info">
                <span className="version-label">Last Updated:</span>
                <span className="version-number">{new Date().toLocaleDateString()}</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <style jsx>{`
        .about-container {
          min-height: 100vh;
          background: #121212;
        }

        .about-content {
          max-width: 900px;
          margin: 0 auto;
          padding: 2rem;
        }

        .page-header {
          margin-bottom: 2.5rem;
        }

        .page-header h1 {
          font-size: 2.5rem;
          font-weight: 700;
          color: #ffffff;
          margin-bottom: 0.5rem;
        }

        .page-header p {
          color: #b0b0b0;
          font-size: 1.1rem;
        }

        .about-sections {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .about-card {
          background: #1a1a1a;
          border: 1px solid #333333;
          border-radius: 8px;
          transition: border-color 0.2s;
        }

        .about-card:hover {
          border-color: #E62F88;
        }

        .about-card :global(h3) {
          color: #ffffff;
          font-size: 1.5rem;
        }

        .card-header-with-icon {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .header-icon {
          color: #E62F88;
          width: 24px;
          height: 24px;
        }

        .about-card :global(.card-content) {
          color: #b0b0b0;
          line-height: 1.8;
          font-size: 1rem;
        }

        .about-card p {
          margin: 0;
        }

        .disclaimer-text {
          background: #2a2a2a;
          padding: 1rem;
          border-radius: 6px;
          border-left: 3px solid #E62F88;
        }

        .privacy-section {
          margin-bottom: 1rem;
        }

        .privacy-section h4 {
          color: #E62F88;
          font-size: 1.1rem;
          font-weight: 600;
          margin-bottom: 0.75rem;
        }

        .privacy-section ul {
          list-style-type: disc;
          padding-left: 1.5rem;
          margin-top: 0.5rem;
        }

        .privacy-section li {
          margin-bottom: 0.5rem;
          color: #b0b0b0;
          line-height: 1.6;
        }

        .privacy-section li strong {
          color: #ffffff;
        }

        .contact-section {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
          padding: 1rem;
          background: #2a2a2a;
          border-radius: 8px;
        }

        .contact-item {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .contact-item strong {
          color: #E62F88;
          font-size: 1rem;
        }

        .email-link {
          color: #ffffff;
          text-decoration: none;
          font-size: 1.1rem;
          transition: color 0.2s;
        }

        .email-link:hover {
          color: #E62F88;
          text-decoration: underline;
        }

        .contact-note {
          font-size: 0.85rem;
          color: #999999;
          font-style: italic;
        }

        .response-note {
          font-size: 0.9rem;
          color: #999999;
          font-style: italic;
        }

        .version-card {
          background: #2a2a2a;
          border: 1px solid #E62F88;
        }

        .version-content {
          display: flex;
          justify-content: space-around;
          align-items: center;
          padding: 1.5rem !important;
        }

        .version-info {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.5rem;
        }

        .version-label {
          color: #b0b0b0;
          font-size: 0.9rem;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .version-number {
          color: #E62F88;
          font-size: 1.25rem;
          font-weight: 700;
        }

        @media (max-width: 768px) {
          .about-content {
            padding: 1rem;
            padding-bottom: 100px;
          }

          .page-header {
            margin-bottom: 1.5rem;
          }

          .page-header h1 {
            font-size: 1.75rem;
          }

          .page-header p {
            font-size: 1rem;
          }

          .about-sections {
            gap: 1rem;
          }

          .about-card :global(h3) {
            font-size: 1.25rem;
          }

          .about-card :global(.card-content) {
            font-size: 0.95rem;
          }

          .contact-section {
            padding: 0.75rem;
          }

          .email-link {
            font-size: 1rem;
            word-break: break-all;
          }

          .version-content {
            flex-direction: column;
            gap: 1rem;
          }

          .version-info {
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
};

export default About;
